import { ReactElement, useState } from 'react';
import { EquipmentAutoComplete, ChatEquipmentSelect } from './ChatEquipmentSelect.tsx';
import { useChat } from './useChat.hook.ts';
import { Brand } from '../../file/model';
import styles from './startConversation.module.css';
import { messageSource, NJChatBlock } from './components/NJChatBlock.tsx';
import { MarkdownMessage } from '@genai/chat';
import { Assistant } from '@genai/assistant';
import { NJButton } from '@engie-group/fluid-design-system-react';
import { equipmentType, EquipmentType } from '../model';

type IStartConversationProps = {
  assistant: Assistant;
  startConversation: (assistant: Assistant, brand: string, model: string) => void;
}

export const StartConversation = ({ assistant, startConversation }: IStartConversationProps): ReactElement => {
  const [selectedEquipmentType, setSelectedEquipmentType] = useState<EquipmentType>(equipmentType.heating);
  const { brands } = useChat(selectedEquipmentType);

  const [selectedBrand, setSelectedBrand] = useState<string | undefined>(undefined);
  const [selectedModel, setSelectedModel] = useState<string | undefined>(undefined);

  const mapBrandsToBrandsAutoComplete = (brands: Brand[], model?: string) => {
    return brands
      .filter(b => !model || b.models.includes(model))
      .map(b => ({ name: b.name, value: b.name } as EquipmentAutoComplete));
  };

  const mapBrandsToModelsAutoComplete = (brands: Brand[], brand?: string) => {
    const flatModels = brand
      ? brands.find(b => b.name === brand)?.models ?? []
      : brands.flatMap(b => b.models);

    return getUniqueModels(flatModels);
  };

  const onBrandSelectionChange = (value: EquipmentAutoComplete | undefined) => {
    setSelectedBrand(value?.value);
    setSelectedModel(undefined);
  };

  const onModelSelectionChange = (value: EquipmentAutoComplete | undefined) => {
    setSelectedModel(value?.value);
  };

  const onEquipmentTypeChange = (newEquipmentType: EquipmentType) => {
    setSelectedEquipmentType(newEquipmentType);
    setSelectedBrand(undefined);
    setSelectedModel(undefined);
  };

  const callStartConversation = async () => {
    if (assistant && selectedBrand && selectedModel) {
      startConversation(assistant, selectedBrand, selectedModel);
    }
  };

  const getUniqueModels = (flatModels: string[]) =>
    Array.from(new Set(flatModels))
      .map(model => ({ name: model, value: model } as EquipmentAutoComplete));

  return (
    <div className={styles['genai-start-conversation']}>
      <h1 className={styles['genai-start-conversation__title']}>Bienvenue,</h1>

      <div className={styles['genai-start-conversation__content']}>
        <NJChatBlock
          source={messageSource.received}
          className={
            styles['genai-start-conversation-chat-exchange-received']
          }
        >
          <MarkdownMessage
            content={assistant?.introduction
              || 'Je suis TechHelp, votre co-pilote virtuel dédié. Je suis conçu pour vous accompagner dans vos interventions. Entrainé pour être sûr, précis et sécurisé. J’espère pouvoir vous être utile au cours de nos conversations. Avant de commencer à travailler ensembles, je souhaiterais en savoir plus sur la chaudière sur laquelle vous vous apprêtez à travailler.'}
          />
          {}
        </NJChatBlock>

        <div className={styles['genai-start-conversation__equipment-select']}>
          <ChatEquipmentSelect
            brands={mapBrandsToBrandsAutoComplete(brands, selectedModel)}
            models={mapBrandsToModelsAutoComplete(brands, selectedBrand)}
            onBrandSelectionChange={onBrandSelectionChange}
            onModelSelectionChange={onModelSelectionChange}
            onEquipmentTypeChange={onEquipmentTypeChange}
          />
        </div>
      </div>

      <div className={styles['genai-start-conversation-button']}>
        <NJButton
          label="Démarrer la conversation"
          variant="ai"
          onClick={() => callStartConversation()}
          disabled={!selectedBrand || !selectedModel}
        />
      </div>
    </div>
  );
};
